import dayjs from 'dayjs';
import _ from 'lodash';

import { PropertyType } from 'constants/loanApplication';
import { numberOfUnitsOptions, occupancyOptions, propertyTypeOptions } from 'constants/options';

/**
 * Maps enrichment API property type strings to loan form PropertyType enum values.
 * The API returns values like "SFR", "Single Family Residential", "Condominium", etc.
 * The form expects PropertyType enum values like "SingleFamily", "Condo", etc.
 */
const PROPERTY_TYPE_TRANSLATIONS = {
  sfr: PropertyType.SINGLE_FAMILY,
  'single family': PropertyType.SINGLE_FAMILY,
  'single family residential': PropertyType.SINGLE_FAMILY,
  'single-family': PropertyType.SINGLE_FAMILY,
  residential: PropertyType.SINGLE_FAMILY,
  condo: PropertyType.CONDO,
  condominium: PropertyType.CONDO,
  townhouse: PropertyType.TOWNHOUSE,
  townhome: PropertyType.TOWNHOUSE,
  'town house': PropertyType.TOWNHOUSE,
  'multi-family': PropertyType.MULTI_UNIT,
  'multi family': PropertyType.MULTI_UNIT,
  multifamily: PropertyType.MULTI_UNIT,
  duplex: PropertyType.MULTI_UNIT,
  triplex: PropertyType.MULTI_UNIT,
  quadplex: PropertyType.MULTI_UNIT,
  'manufactured home': PropertyType.MANUFACTURED_HOME,
  manufactured: PropertyType.MANUFACTURED_HOME,
  'mobile home': PropertyType.MANUFACTURED_HOME,
  mobile: PropertyType.MANUFACTURED_HOME,
  pud: PropertyType.PUD,
  'planned unit development': PropertyType.PUD,
  cooperative: PropertyType.COOP,
  'co-op': PropertyType.COOP,
  coop: PropertyType.COOP,
};

const translatePropertyType = value => {
  if (_.isNil(value)) {
    return null;
  }

  const normalizedValue = String(value).toLowerCase().trim();
  const translated = PROPERTY_TYPE_TRANSLATIONS[normalizedValue];

  if (translated) {
    return translated;
  }

  const partialMatch = Object.keys(PROPERTY_TYPE_TRANSLATIONS).find(key =>
    normalizedValue.includes(key),
  );

  return partialMatch ? PROPERTY_TYPE_TRANSLATIONS[partialMatch] : null;
};

/**
 * Translates enrichment API "units" count to the numberOfUnits select option value.
 * The form select only has options for 2, 3, and 4 units.
 */
const MIN_MULTI_UNITS = 2;
const MAX_MULTI_UNITS = 4;

const translateNumberOfUnits = value => {
  if (_.isNil(value)) {
    return null;
  }

  const numValue = Number(value);

  if (numValue >= MIN_MULTI_UNITS && numValue <= MAX_MULTI_UNITS) {
    return numValue;
  }

  return null;
};

/**
 * Translates enrichment API owner-occupied boolean to occupancy form value.
 * true -> 'PrimaryResidence', false -> null (cannot determine Investment vs SecondHome)
 */
const OWNER_OCCUPIED_OCCUPANCY = 'PrimaryResidence';

/**
 * Translates enrichment API year (e.g. 1985) to an ISO date string.
 * The form field is a DATE picker with format 'YYYY', so it expects a
 * dayjs-parseable date string, not a raw number.
 */
const MIN_VALID_YEAR = 1600;
const MAX_VALID_YEAR = 2100;

const translateYearBuilt = value => {
  if (_.isNil(value)) {
    return null;
  }

  const year = Number(value);

  if (Number.isNaN(year) || year < MIN_VALID_YEAR || year > MAX_VALID_YEAR) {
    return null;
  }

  return dayjs().year(year).startOf('year').toISOString();
};

const translateOccupancy = value => {
  if (value === true || value === 'true' || value === 'Yes') {
    return OWNER_OCCUPIED_OCCUPANCY;
  }

  return null;
};

/**
 * Formats a numeric value as US currency (e.g., 338112 -> "$338,112").
 */
const formatCurrency = value => {
  if (_.isNil(value)) {
    return '';
  }

  const num = Number(value);

  if (Number.isNaN(num)) {
    return String(value);
  }

  return num.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

/**
 * Looks up the human-readable label for a select option value.
 */
const getSelectOptionLabel = (options, value) => {
  const match = options.find(opt => opt.value === value);

  return match ? match.name : String(value);
};

const formatPropertyTypeDisplay = value => getSelectOptionLabel(propertyTypeOptions, value);
const formatOccupancyDisplay = value => getSelectOptionLabel(occupancyOptions, value);
const formatNumberOfUnitsDisplay = value => getSelectOptionLabel(numberOfUnitsOptions, value);

/**
 * Maps PropertyEnrichmentResult field names (from the backend API)
 * to loan form field nameInModel values.
 *
 * Each entry: { enrichmentKey, formFieldName, label, translateValue?, isSelect?, formatDisplay? }
 * - enrichmentKey: property name in the enrichment API response (supports dot paths)
 * - formFieldName: nameInModel used in the form config
 * - label: human-readable label for display in hints
 * - translateValue: optional function to translate API value to form enum value
 * - isSelect: true if the form field stores values as { value: 'x' } objects
 * - formatDisplay: optional function to format the value for popover display
 */

/** Property editor fields */
const PROPERTY_FIELD_MAPPING = [
  {
    enrichmentKey: 'propertyType',
    formFieldName: 'propertyType',
    label: 'Property Type',
    translateValue: translatePropertyType,
    isSelect: true,
    formatDisplay: formatPropertyTypeDisplay,
  },
  {
    enrichmentKey: 'units',
    formFieldName: 'numberOfUnits',
    label: 'Number of Units',
    translateValue: translateNumberOfUnits,
    isSelect: true,
    formatDisplay: formatNumberOfUnitsDisplay,
  },
  {
    enrichmentKey: 'ownerOccupied',
    formFieldName: 'occupancy',
    label: 'Occupancy',
    translateValue: translateOccupancy,
    isSelect: true,
    formatDisplay: formatOccupancyDisplay,
  },
  {
    enrichmentKey: 'estimatedValue',
    formFieldName: 'appraisedValue',
    label: 'Estimated Value \u2192 Appraised Value',
    formatDisplay: formatCurrency,
  },
  {
    enrichmentKey: 'estimatedValue',
    formFieldName: 'propertyValue',
    label: 'Estimated Value',
    formatDisplay: formatCurrency,
  },
  { enrichmentKey: 'fips', formFieldName: 'countyFipsCode', label: 'FIPS Code' },
  { enrichmentKey: 'county', formFieldName: 'county', label: 'County' },
];

/** Appraisal editor fields */
const APPRAISAL_FIELD_MAPPING = [
  {
    enrichmentKey: 'yearBuilt',
    formFieldName: 'appraisal.neighbourhoodAndSite.yearBuilt',
    label: 'Year Built',
    translateValue: translateYearBuilt,
  },
  {
    enrichmentKey: 'stories',
    formFieldName: 'appraisal.neighbourhoodAndSite.numberOfStories',
    label: 'Number of Stories',
  },
  {
    enrichmentKey: 'squareFeet',
    formFieldName: 'appraisal.propertyDetails.propertySquareFootage',
    label: 'Square Footage',
  },
  {
    enrichmentKey: 'apn',
    formFieldName: 'appraisal.neighbourhoodAndSite.assessorParcelNumber',
    label: "Assessor's Parcel Number",
  },
  {
    enrichmentKey: 'lot',
    formFieldName: 'appraisal.neighbourhoodAndSite.lot',
    label: 'Lot',
  },
  {
    enrichmentKey: 'block',
    formFieldName: 'appraisal.neighbourhoodAndSite.block',
    label: 'Block',
  },
  {
    enrichmentKey: 'subdivision',
    formFieldName: 'appraisal.neighbourhoodAndSite.plat',
    label: 'Plat / Subdivision',
  },
  {
    enrichmentKey: 'acreage',
    formFieldName: 'appraisal.neighbourhoodAndSite.propertyAcreage',
    label: 'Property Acreage',
  },
  {
    enrichmentKey: 'censusTract',
    formFieldName: 'appraisal.neighbourhoodAndSite.censusTract',
    label: 'Census Tract',
  },
  {
    enrichmentKey: 'assessedLandValue',
    formFieldName: 'appraisal.neighbourhoodAndSite.lotAppraisedValue',
    label: 'Lot Appraised Value',
    formatDisplay: formatCurrency,
  },
  /**
   * Per-unit fields: The enrichment API provides aggregate values (not per-unit
   * breakdowns), so these map to the primary unit (index 0) only. Multi-unit
   * properties would need per-unit data from the API for more granular mapping.
   */
  {
    enrichmentKey: 'bedrooms',
    formFieldName: 'appraisal.uniformCollateralDataPortal.units[0].bedroomsNumber',
    label: 'Bedrooms (Unit 1)',
  },
  {
    enrichmentKey: 'bathrooms',
    formFieldName: 'appraisal.uniformCollateralDataPortal.units[0].bathsNumber',
    label: 'Bathrooms (Unit 1)',
  },
  {
    enrichmentKey: 'rooms',
    formFieldName: 'appraisal.uniformCollateralDataPortal.units[0].roomsNumber',
    label: 'Rooms (Unit 1)',
  },
];

/** Project editor fields */
const PROJECT_FIELD_MAPPING = [
  {
    enrichmentKey: 'units',
    formFieldName: 'project.projectInfo.projectUnitsNumber',
    label: 'Number of Units (Project)',
  },
];

/**
 * Converts annual amount to monthly, preserving the original annual value
 * for accurate display (avoids rounding drift from dividing then multiplying).
 */
const MONTHS_PER_YEAR = 12;

const annualToMonthly = value => {
  if (_.isNil(value)) {
    return null;
  }

  const num = Number(value);

  if (Number.isNaN(num) || num <= 0) {
    return null;
  }

  return Number((num / MONTHS_PER_YEAR).toFixed(2));
};

/**
 * Display formatter for values that were already converted from annual to monthly
 * by translateValue (annualToMonthly). Stores the original annual value in the
 * field metadata to avoid rounding drift when reconstructing annual from monthly.
 */
const formatCurrencyMonthly = (monthlyValue, _fieldName, originalAnnual) => {
  if (_.isNil(monthlyValue)) {
    return '';
  }

  const annual = !_.isNil(originalAnnual)
    ? Number(originalAnnual)
    : Number(monthlyValue) * MONTHS_PER_YEAR;

  return `${formatCurrency(monthlyValue)}/mo (${formatCurrency(annual)}/yr)`;
};

/** Property Expenses fields */
const PROPERTY_EXPENSES_FIELD_MAPPING = [
  {
    enrichmentKey: 'estimatedMortgagePayment',
    formFieldName: 'existingPropertyExpenses.principalAndInterest',
    label: 'Principal & Interest (from Mortgage Info)',
    formatDisplay: formatCurrency,
  },
  {
    enrichmentKey: 'taxAmount',
    formFieldName: 'existingPropertyExpenses.propertyTaxes',
    label: 'Property Taxes (Annual → Monthly)',
    translateValue: annualToMonthly,
    formatDisplay: formatCurrencyMonthly,
    preserveOriginalForDisplay: true,
  },
  {
    enrichmentKey: 'associationFee',
    formFieldName: 'existingPropertyExpenses.homeownersAssociation',
    label: 'HOA Dues',
    formatDisplay: formatCurrency,
  },
  {
    enrichmentKey: 'associationFee',
    formFieldName: 'proposedPropertyExpenses.associationFeesAmount',
    label: 'HOA Dues (Proposed)',
    formatDisplay: formatCurrency,
  },
];

const ENRICHMENT_FIELD_MAPPING = [
  ...PROPERTY_FIELD_MAPPING,
  ...APPRAISAL_FIELD_MAPPING,
  ...PROJECT_FIELD_MAPPING,
  ...PROPERTY_EXPENSES_FIELD_MAPPING,
];

/**
 * Returns a map of formFieldName -> enrichment value for fields that
 * have enrichment data available. Values are translated to form-compatible
 * enum values where applicable.
 */
const buildEnrichmentValuesMap = (mapping, enrichmentData) => {
  if (!enrichmentData) {
    return {};
  }

  return mapping.reduce(
    (acc, { enrichmentKey, formFieldName, translateValue, preserveOriginalForDisplay }) => {
      const rawValue = _.get(enrichmentData, enrichmentKey);

      if (_.isNil(rawValue)) {
        return acc;
      }

      const value = translateValue ? translateValue(rawValue) : rawValue;

      if (!_.isNil(value)) {
        acc[formFieldName] = preserveOriginalForDisplay
          ? { value, originalValue: rawValue }
          : value;
      }

      return acc;
    },
    {},
  );
};

export const getEnrichmentValuesMap = enrichmentData =>
  buildEnrichmentValuesMap(PROPERTY_FIELD_MAPPING, enrichmentData);

export const getAppraisalEnrichmentValuesMap = enrichmentData =>
  buildEnrichmentValuesMap(APPRAISAL_FIELD_MAPPING, enrichmentData);

export const getProjectEnrichmentValuesMap = enrichmentData =>
  buildEnrichmentValuesMap(PROJECT_FIELD_MAPPING, enrichmentData);

export const getPropertyExpensesEnrichmentValuesMap = enrichmentData =>
  buildEnrichmentValuesMap(PROPERTY_EXPENSES_FIELD_MAPPING, enrichmentData);

/**
 * Returns a lookup map of formFieldName -> field metadata for quick access
 * to isSelect and formatDisplay per field.
 */
const FIELD_META_MAP = ENRICHMENT_FIELD_MAPPING.reduce((acc, field) => {
  acc[field.formFieldName] = field;

  return acc;
}, {});

/**
 * Returns true if the given form field is a select (stores { value } objects).
 */
const isSelectField = formFieldName => !!FIELD_META_MAP[formFieldName]?.isSelect;

/**
 * Formats an enrichment value for display in the popover, respecting the
 * destination field's data format (currency, select option label, etc.).
 * For fields with preserveOriginalForDisplay, the original untranslated value
 * is passed to the formatter to avoid rounding drift.
 */
export const formatEnrichmentDisplay = (formFieldName, value, originalValue) => {
  const meta = FIELD_META_MAP[formFieldName];

  if (meta?.formatDisplay) {
    return meta.preserveOriginalForDisplay
      ? meta.formatDisplay(value, formFieldName, originalValue)
      : meta.formatDisplay(value);
  }

  return _.isNil(value) ? '' : String(value);
};

/**
 * Wraps a plain enrichment value into the shape expected by setInForm.
 * Select fields need { value: x }, other fields get the raw value.
 */
export const toFormValue = (formFieldName, value) => {
  if (isSelectField(formFieldName)) {
    return { value };
  }

  return value;
};
